import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Table, Typography, Select, Skeleton } from 'antd';
import { useIntl } from 'react-intl';
import { getUser } from '../utils/auth';
import { useHistory } from 'react-router-dom';
import { getFileLink } from '../utils/get-file-link';
import { APIs, Pages, PageTypes } from '../utils/constants';
import Text from '../components/text';
import DashboardFooter from '../components/dashboard-footer';
import Helmet from 'react-helmet';
import './match.scss';

function Match() {
    const { id } = useParams();
    const user = getUser();
    const history = useHistory();

    // States
    const [pageType, setPageType] = useState('');
    const [tableTitle, setTableTitle] = useState('');
    const [tableColumns, setTableColumns] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(true);

    // CSS
    const styles = {
        tableTitleStyle: {
            maxWidth: '300px'
        }
    }

    // Actions
    useEffect(async () => {
        setLoading(true);
        const loadingColumns = [];
        for (let i = 0; i < 5; i++) {
            const loadingColumn = {
                key: i,
                title: <Skeleton.Button active='true' size='small' />,
                dataIndex: 'data'
            };
            loadingColumns.push(loadingColumn);
        }

        const loadingDatas = [];
        for (let i = 0; i < 15; i++) {
            const loadingData = { key: i, data: <Skeleton.Button active='true' size='small' className='w-100' /> };
            loadingDatas.push(loadingData);
        }

        setTableColumns(loadingColumns);
        setTableData(loadingDatas);
        setTableTitle(<Skeleton.Button active='true' />);

        try {
            const response = await fetch(APIs.GET_MATCH, {
                method: 'POST', body: JSON.stringify({
                    user: user.id,
                    token: user.deviceIdentity,
                    ctoken: user.ctoken,
                    id: id
                })
            });
            const r = await response.json();
            if (response.ok) {
                setPageType(r.type);
                let data = [];
                let columns = [];
                columns = r.cols ? r.cols.map((col, index) => {
                    return {
                        key: index,
                        title: <Text id={'match::table::header::' + col.name} />,
                        dataIndex: col.name,
                        render: text => colRender(text, col, r.type),
                        width: col.width,
                        align: col.align ? col.align : 'left',
                    }
                }) : [];

                setTableTitle(<Text id={'match::table::title::' + r.type} values={{
                    name: () => <Typography.Link style={styles.tableTitleStyle}
                        ellipsis={r.name} onClick={() => getFileLink(id, history, setErrorMessage)} className='fw-light'>{r.name}</Typography.Link>
                }} />);

                if (r.type === PageTypes.JOB) {
                    data = r.items ? r.items.map((item, index) => {
                        return {
                            key: index,
                            name: item.name,
                            match: item.percentage,
                            email: item.email,
                            phone: item.phone,
                            stage: item.stage
                        }
                    }) : [];
                } else {
                    data = r.items ? r.items.map((item, index) => {
                        return {
                            key: index,
                            position: item.position,
                            match: item.percentage,
                            uploaded: item.uploaded,
                            stage: item.stage
                        }
                    }) : [];
                }

                setTableData([]);
                setTableColumns(columns);
                setTableData(data);
                setErrorMessage(false);
            } else if (response.status == 403) {
                localStorage.removeItem('user');
                history.push(Pages.HOME);
            } else {
                setErrorMessage(<Text id='match::error::fetch' />);
            }
        } catch (error) {
            setErrorMessage(<Text id='match::error::fetch' />);
        }
        setLoading(false);
    }, []);

    const colRender = (text, col, pageType) => {
        if (col.isLink) {
            return <a className='text-primary' onClick={() => getFileLink(text.fid, history, setErrorMessage)}>
                {text.value}
            </a>
        } else if (col.isMatch) {
            return {
                props: {
                    className: parseInt(text.value) >= 80 ? 'bg-green' : parseInt(text.value) >= 60 ? 'bg-yellow' : 'bg-red'
                },
                children: <div>{text.value + '%'}</div>
            }
        } else if (col.isStage) {
            return <Select
                defaultValue={text.value ? text.value : 'Awaiting Review'}
                className='w-75 bg-light-blue'
                onChange={(value) => handleStageChange(value, text.ref_id, pageType)}>
                <Select.Option value='Awaiting Review'>
                    <Text id='match::table::stage::option::awaiting-review' />
                </Select.Option>
                <Select.Option value='Reviewed'>
                    <Text id='match::table::stage::option::reviewed' />
                </Select.Option>
                <Select.Option value='Contacting'>
                    <Text id='match::table::stage::option::contacting' />
                </Select.Option>
                <Select.Option value='Rejected'>
                    <Text id='match::table::stage::option::rejected' />
                </Select.Option>
                <Select.Option value='Hired'>
                    <Text id='match::table::stage::option::hired' />
                </Select.Option>
            </Select>
        } else if (col.name == 'email') {
            return <a href={'mailto: ' + text.value}>
                {text.value}
            </a>
        } else if (col.name == 'phone') {
            return <a href={'tel: ' + text.value}>
                {text.value}
            </a>
        } else {
            return text.value;
        }
    }

    const handleStageChange = async (value, match_id, pageType) => {
        try {
            const response = await fetch(APIs.UPDATE_STAGE, {
                method: 'POST', body: JSON.stringify({
                    user: user.id,
                    token: user.deviceIdentity,
                    ctoken: user.ctoken,
                    id: id,
                    match_id: match_id,
                    stage: value,
                    type: pageType
                })
            });
            if (response.status == 403) {
                localStorage.removeItem('user');
                history.push(Pages.HOME);
            } else if (!response.ok) {
                setErrorMessage(<Text id='match::error::update-stage' />);
            }
        } catch (error) {
            setErrorMessage(<Text id='match::error::update-stage' />);
        }
    }

    return (
        <>
            <Helmet>
                <title>
                    {`${useIntl().formatMessage({ id: 'match::text::title-name' },
                        { type: pageType.charAt(0).toUpperCase() + pageType.slice(1) })}`}
                </title>
            </Helmet>
            <div className='row vh-100'>
                <div className='col-12 pt-5 px-4 position-relative'>
                    {errorMessage &&
                        <Alert
                            showIcon={false}
                            type='error'
                            banner
                            className='position-absolute w-100 text-danger top-0 start-0 text-center'
                            message={errorMessage}
                        />
                    }
                    <div className='d-flex'>
                        <Typography.Title level={3} className='m-0'>
                            {tableTitle}
                        </Typography.Title>
                        {!loading && <div className='mt-auto mb-0 ms-5 fs-5 fw-light'>
                            <Typography.Text>
                                <Text id='match::table::sub-title' values={{
                                    number: () => <strong>{tableData.length}</strong>
                                }} />
                            </Typography.Text>
                        </div>}
                    </div>
                    <Table
                        columns={tableColumns}
                        pagination={{ position: ['topRight'], pageSize: 15, showSizeChanger: false }}
                        dataSource={tableData}
                    />
                </div>
                <div className='mt-auto'>
                    <DashboardFooter />
                </div>
            </div>
        </>
    );
}

export default Match;
